import { Controller } from "@hotwired/stimulus"

// Native ellipsis leaves the unused fraction of its last character before the
// extension. Fit that box to the rendered prefix and ellipsis, keeping the
// original text in the DOM for copying and accessibility.
export default class extends Controller {
  connect() {
    this.stem = this.element.querySelector(".filename-stem")
    if (!this.stem || !this.element.querySelector(".filename-ext")) return

    this.measure = document.createElement("canvas").getContext("2d")
    if (!this.measure) return
    this.fit = this.fit.bind(this)
    this.observer = new ResizeObserver(this.fit)

    // Observe the containing line: the fitted name itself need not grow when
    // a drawer or viewport widens again.
    let container = this.element.parentElement
    while (container?.parentElement && getComputedStyle(container).display.startsWith("inline")) {
      container = container.parentElement
    }
    this.observer.observe(container)
    document.fonts.ready.then(() => {
      if (this.element.isConnected && this.observer) this.fit()
    })
    this.fit()
  }

  disconnect() {
    this.observer?.disconnect()
    this.observer = null
    this.stem?.style.removeProperty("max-width")
  }

  fit() {
    this.stem.style.removeProperty("max-width")
    const available = this.stem.getBoundingClientRect().width
    if (this.stem.scrollWidth <= this.stem.clientWidth) return

    this.measure.font = getComputedStyle(this.stem).font
    const characters = Array.from(
      new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(this.stem.textContent),
      part => part.segment
    )
    // The browser draws the ellipsis as its own run, so it is never kerned
    // into the last letter, and it keeps a space the cut lands after. Measure
    // the same way, and never cut right after a space: either would leave the
    // box wider than the text and open a gap before the extension.
    const ellipsis = this.measure.measureText("…").width
    const width = count => this.measure.measureText(characters.slice(0, count).join("")).width + ellipsis
    let low = 0
    let high = characters.length
    while (low < high) {
      const middle = Math.ceil((low + high) / 2)
      if (width(middle) <= available) low = middle
      else high = middle - 1
    }
    while (low > 0 && /\s/.test(characters[low - 1])) low -= 1
    this.stem.style.maxWidth = `${Math.min(available, Math.ceil(width(low)))}px`
  }
}
